import React, { useMemo } from 'react';
import {
  Button,
  Column,
  Copy,
  Flex,
  Heading,
  Row,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { isPast } from 'date-fns';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { ILineOfCreditResponseType } from 'src/types/LineOfCredit';
import {
  getDayLocFrequency,
  getFirstInstanceDateFormatted,
  getFormattedLocBudgetAmount,
  getWeekLocDates,
} from 'src/models/LineOfCreditModelUtils';

export interface LineOfCreditDetailsCardProps {
  loc: ILineOfCreditResponseType;
  onEditClick: () => void;
  onDeleteClick: () => void;
}

export const LineOfCreditDetailsCard: React.FC<LineOfCreditDetailsCardProps> =
  ({ loc, onEditClick, onDeleteClick }) => {
    const { isPhone } = useScreenSizes();

    const locDescriptionText = useMemo<string>(() => {
      if (loc.budget_reset_period === 1) {
        return `${getFormattedLocBudgetAmount(loc)} ${
          getDayLocFrequency(loc).short
        }`;
      }
      if (loc.repeating) {
        // repeats
        return `${getFormattedLocBudgetAmount(loc)} every week`;
      }
      // date range
      return `${getFormattedLocBudgetAmount(loc)} ${getWeekLocDates({
        loc,
        format: 'LLLL d',
        useDashForDateRange: true,
      })}`;
    }, [loc]);

    const startDateLabel = useMemo<string>(() => {
      if (loc.repeating) {
        const isDateInThePast = isPast(new Date(loc.first_instance_utc));

        if (isDateInThePast) {
          return 'Started on';
        }

        return 'Starts on';
      }
      return loc.budget_reset_period === 1 ? 'Date' : 'Dates';
    }, [loc.repeating, loc.first_instance_utc, loc.budget_reset_period]);

    const yearText = `${getFirstInstanceDateFormatted(
      loc.first_instance_utc,
      'yyyy'
    )}`;

    const startDateText = useMemo<string>(() => {
      if (loc.repeating) {
        return `${getFirstInstanceDateFormatted(
          loc.first_instance_utc,
          'LLLL d'
        )}, ${yearText}`;
      }
      return loc.budget_reset_period === 7
        ? `${getWeekLocDates({
            loc,
            format: 'LLLL d',
            useDashForDateRange: true,
          }).trim()}, ${yearText} `
        : `${getFirstInstanceDateFormatted(
            loc.first_instance_utc,
            'LLLL d'
          ).trim()}, ${yearText} `;
    }, [loc, yearText]);

    const frequencyText = useMemo<string>(() => {
      if (!loc.repeating) {
        return 'Does not repeat';
      }
      return loc.budget_reset_period === 1
        ? `Repeats ${getDayLocFrequency(loc).long}`
        : 'Repeats every week';
    }, [loc]);

    return (
      <Stack y="md">
        <Square
          inset={!isPhone ? 'lg' : 'md'}
          dimension="2"
          bg="untintedBackground"
        >
          <Row>
            <Column phone={12} tablet={8} desktop={9}>
              <Stack y={isPhone ? 'lg' : 'none'} width="100%">
                <Stack y="sm">
                  <Heading variant="h1">{loc.name}</Heading>
                </Stack>
                <Stack y="md">
                  <Heading variant="h4">{locDescriptionText}</Heading>
                </Stack>
                <Flex flexDirection={isPhone ? 'column' : 'row'}>
                  <Stack
                    y={isPhone ? 'md' : 'none'}
                    x={!isPhone ? 'lg' : 'none'}
                  >
                    <Heading variant="h5" data-testid="date-label">
                      {startDateLabel}
                    </Heading>
                    <Copy variant="body"> {startDateText}</Copy>
                  </Stack>
                  <Flex flexDirection="column">
                    <Heading variant="h5">Frequency</Heading>
                    <Copy variant="body"> {frequencyText}</Copy>
                  </Flex>
                </Flex>
              </Stack>
            </Column>
            <Column phone={12} tablet={4} desktop={3}>
              <Flex
                flexDirection="column"
                height="100%"
                justifyContent="center"
              >
                <Stack y="xxx-sm">
                  <Button
                    block
                    variant="secondary"
                    buttonSize="medium"
                    onClick={onEditClick}
                  >
                    Edit
                  </Button>
                </Stack>

                <Button
                  block
                  variant="tertiary"
                  buttonSize="medium"
                  onClick={onDeleteClick}
                >
                  Delete
                </Button>
              </Flex>
            </Column>
          </Row>
        </Square>
      </Stack>
    );
  };
